import React, { useCallback, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import _, { clone } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import validate from 'utils/validate';
import FormAdd2 from '../../../components/FormAdd2';
import { ADD_COURIER_SUCCESS, dspSelector } from '../../../reducers/DspReducer';
import { postDsp } from '../../../repositories/HttpRepository';
import FormatPhoneNumber from '../../../utils/FormatPhoneNumber';
import { getRegions } from '../../../atoms/BookingTemplate';
import { COURIER_TYPES, getFormMetadata, VALIDATE_RULES } from '../FormMetadata';
import { getSelectorPlatform } from 'atoms/DspState';

const useInputStyles = makeStyles()(
  {
    root: {
      fontWeight: 600,
      fontFamily: 'AvenirNext',
      fontStretch: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontSize: 14,
    },
  },
  { name: 'MuiInputBase' },
);
const useInputLabelStyles = makeStyles()(
  {
    root: {
      fontWeight: 600,
      fontFamily: 'AvenirNext',
      fontStretch: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      fontSize: 14,
    },
  },
  { name: 'MuiFormLabel' },
);
const useStyles = makeStyles()((theme) => ({
  DialogAction: {
    margin: '10px 30px',
  },
  title: {
    fontWeight: 700,
  },
  closeBtn: {
    position: 'absolute',
    right: 20,
    top: 15,
    color: '#d8d8d8',
    fontSize: 30,
    cursor: 'pointer',
  },
  CancelBtn: {
    border: 'solid 1px #4a4a4a',
    backgroundColor: '#ffffff',
    padding: '3px 15px',
    color: '#000',
  },
  SaveBtn: {
    backgroundColor: '#cccccc !important',
    padding: '4px 25px',
    color: '#fff',
    '&.active': {
      backgroundColor: '#4abc4e !important',
    },
  },
  DialogContent: {
    overflowY: 'initial',
  },
}));

const DEFAULT_VALUE = {
  username: '',
  password: '',
  email: '',
  company: '',
  phone_number: '',
  regions: [],
  type: '',
  platform: '',
};

let newCourierData = { ...DEFAULT_VALUE };

const AddCourier = () => {
  const updateValues = useSetRecoilState(dspSelector);
  const regions = useRecoilValue(getRegions);
  const platforms = useRecoilValue(getSelectorPlatform);
  const optPlatforms =
    platforms?.courier_platforms
      ?.split(',')
      ?.filter(Boolean)
      ?.map((p) => ({ label: p?.toUpperCase(), value: p })) || [];
  const [courier, setCourier] = useState(DEFAULT_VALUE);
  const [errorForm, setErrorForm] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const navigate = useNavigate();
  const { classes } = useStyles();
  useInputStyles();
  useInputLabelStyles();

  const handleSelectRegionChange = (_, selected) => {
    newCourierData.regions = selected.map(({ value }) => value);
    setCourier(newCourierData);
    const clearError = clone(errorForm);
    clearError['regions'] = '';
    setErrorForm(clearError);
  };

  const handleSelectTypeChange = (_, selected) => {
    newCourierData.type = selected.props.value;
    newCourierData.platform = '';
    setCourier(newCourierData);
    const clearError = clone(errorForm);
    clearError['type'] = '';
    setErrorForm(clearError);
  };

  const handlePlatformChange = (_, selected) => {
    newCourierData.platform = selected.props.value;
    setCourier(newCourierData);
    const clearError = clone(errorForm);
    clearError['platform'] = '';
    setErrorForm(clearError);
  };

  const regionOptions = _.sortBy(
    regions?.map(({ properties }) => ({
      label: `[${properties.code}] ${properties.display_name}`,
      value: properties.code,
    })),
    'value',
  );

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    newCourierData[name] = value;
    setCourier(newCourierData);
    // clear error
    const clearError = clone(errorForm);
    clearError[name] = '';
    setErrorForm(clearError);
  };

  const submitForm = useCallback(() => {
    if (isSubmit) return;
    setIsSubmit(true);
    //format phone number
    const newPhoneNumber = FormatPhoneNumber(newCourierData.phone_number);
    newCourierData.phone_number = newPhoneNumber;

    setCourier(newCourierData);
    let rules = VALIDATE_RULES;
    if (newCourierData.type !== 'PLATFORM') {
      delete rules.platform;
    }
    const error = validate(courier, rules);
    if (error) {
      setErrorForm(error);
      setIsSubmit(false);
    } else {
      postDsp(courier).then((res) => {
        setIsSubmit(false);
        if (res.ok) {
          updateValues({
            type: ADD_COURIER_SUCCESS,
            data: res.data,
          });
          toast.success('DSP added successfully');
          newCourierData = { ...DEFAULT_VALUE };
          navigate('/dsp');
        } else if (res.data?.errors) {
          toast.error(res.data.errors[0]);
        } else {
          toast.error('Server is having problem, please try again later.');
        }
      });
    }
  }, [courier, isSubmit]);

  function handleCancel() {
    newCourierData = { ...DEFAULT_VALUE };
    navigate('/dsp');
  }

  function joinClassnames(...classnames) {
    return classnames.filter((i) => i).join(' ');
  }

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth
      onClose={() => {
        navigate('/dsp');
      }}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        <Typography variant="h4" className={classes.title}>
          Add New DSP Courier
          <CloseIcon className={classes.closeBtn} onClick={handleCancel} />
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.DialogContent}>
        <FormAdd2
          handleChange={handleChange}
          error={errorForm}
          list={getFormMetadata(false, regionOptions, COURIER_TYPES, handleSelectTypeChange, handleSelectRegionChange, optPlatforms, handlePlatformChange, newCourierData.type)}
          datas={newCourierData}
        />
      </DialogContent>
      <DialogActions className={classes.DialogAction}>
        <Button className={classes.CancelBtn} onClick={handleCancel}>
          CANCEL
        </Button>
        <Button className={joinClassnames(classes.SaveBtn, 'active')} onClick={submitForm} disabled={isSubmit}>
          ADD
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourier;
